import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Leaf, User, LogOut, Menu, X, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setToolsOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const mainLinks = [
    { to: "/how-it-works", label: "How it works" },
    { to: "/gallery", label: "Gallery" },
    { to: "/blog", label: "Blog" },
    { to: "/about", label: "About" },
    { to: "/pricing", label: "Pricing" },
  ];

  const toolLinks = [
    { to: "/ai-designer", label: "AI Designer", tag: "Premium" },
    { to: "/plant-er", label: "Plant ER", tag: "Premium" },
    { to: "/marketplace", label: "Marketplace", tag: "Premium" },
    { to: "/material-calculator", label: "Material Calculator", tag: "Free" },
    { to: "/buddy-care", label: "Buddy Care", tag: "Free" },
    { to: "/seasonal-planner", label: "Seasonal Planner", tag: "Free" },
    { to: "/pros", label: "Pro's Network", tag: "Free" },
    { to: "/plants", label: "Plant Library", tag: "Free" },
  ];

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-full text-sm transition-all ${
      isActive ? "bg-[#2D4A3E] text-[#F9F6F0]" : "text-[#1A2F25] hover:bg-[#E5E0D8]"
    }`;

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-full bg-[#2D4A3E] flex items-center justify-center group-hover:bg-[#1F352C] transition-colors">
              <Leaf className="w-5 h-5 text-[#F9F6F0]" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl tracking-tight font-medium">Backyard Buddy</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {mainLinks.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
              </NavLink>
            ))}

            {/* Tools dropdown */}
            <div className="relative" ref={toolsRef}>
              <button
                onClick={() => setToolsOpen(!toolsOpen)}
                className="px-3 py-2 rounded-full text-sm text-[#1A2F25] hover:bg-[#E5E0D8] flex items-center gap-1 transition-all"
                data-testid="nav-tools"
              >
                Tools <ChevronDown className={`w-3 h-3 transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
              </button>
              {toolsOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-[#E5E0D8] rounded-2xl shadow-xl py-2 z-50" data-testid="tools-dropdown">
                  {toolLinks.map((t) => (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      onClick={() => setToolsOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center justify-between px-4 py-2.5 hover:bg-[#F9F6F0] transition-colors text-sm ${isActive ? "bg-[#F9F6F0]" : ""}`
                      }
                      data-testid={`tool-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span>{t.label}</span>
                      <span className={`overline px-1.5 py-0.5 rounded ${t.tag === "Premium" ? "bg-[#D47C59] text-white" : "bg-[#E5E0D8] text-[#2D4A3E]"}`}>
                        {t.tag}
                      </span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                {user.user_type === "pro" ? (
                  <Link to="/pros/dashboard" className="btn-ghost flex items-center gap-2 text-sm" data-testid="nav-pro-dashboard">
                    Pro Dashboard
                  </Link>
                ) : (
                  <NavLink to="/my-backyard" className={linkClass} data-testid="nav-my-backyard">My Backyard</NavLink>
                )}
                <NavLink to="/refer" className={linkClass} data-testid="nav-refer">🎁 Refer</NavLink>
                {user.role === "admin" && (
                  <NavLink to="/admin/marketing" className={linkClass} data-testid="nav-marketing">
                    📣 Marketing
                  </NavLink>
                )}
                <Link to="/profile" className="btn-ghost flex items-center gap-2" data-testid="nav-profile">
                  <User className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{user.name?.split(" ")[0]}</span>
                </Link>
                <button onClick={handleLogout} className="btn-ghost" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">Sign in</Link>
                <Link to="/register" className="btn-primary text-sm" data-testid="nav-register">Get started</Link>
              </>
            )}
          </div>

          <button className="md:hidden btn-ghost" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 flex flex-col gap-1 animate-fade-in max-h-[80vh] overflow-y-auto">
            {mainLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className={linkClass}>{l.label}</NavLink>
            ))}
            <p className="overline text-[#D47C59] px-3 pt-3 pb-1">Tools</p>
            {toolLinks.map((t) => (
              <NavLink key={t.to} to={t.to} onClick={() => setOpen(false)} className={linkClass}>
                {t.label} <span className="text-xs text-[#5C6B62] ml-1">({t.tag})</span>
              </NavLink>
            ))}
            {user ? (
              <>
                {user.user_type === "pro" ? (
                  <NavLink to="/pros/dashboard" onClick={() => setOpen(false)} className={linkClass}>Pro Dashboard</NavLink>
                ) : (
                  <>
                    <NavLink to="/my-backyard" onClick={() => setOpen(false)} className={linkClass}>My Backyard</NavLink>
                    <NavLink to="/my-designs" onClick={() => setOpen(false)} className={linkClass}>My Designs</NavLink>
                    <NavLink to="/projects" onClick={() => setOpen(false)} className={linkClass}>Projects</NavLink>
                  </>
                )}
                <NavLink to="/refer" onClick={() => setOpen(false)} className={linkClass}>🎁 Refer a friend</NavLink>
                <NavLink to="/profile" onClick={() => setOpen(false)} className={linkClass}>Profile</NavLink>
                <button onClick={() => { handleLogout(); setOpen(false); }} className="text-left px-3 py-2 text-sm hover:bg-[#E5E0D8] rounded-full">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className={linkClass}>Sign in</NavLink>
                <NavLink to="/register" onClick={() => setOpen(false)} className={linkClass}>Get started</NavLink>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
